import React, { useEffect, useState } from 'react';
import { Plus, Search, Edit, Trash2, X, Calendar, DollarSign, Building2, User, CheckCircle, XCircle } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useToast } from '../../contexts/ToastContext';

interface HotelReimbursement {
  id: string;
  driver_name: string;
  date: string;
  hotel_description: string;
  who_pays: 'company' | 'driver';
  amount: number;
  driver_paid: boolean;
  created_at: string;
}

interface Driver {
  id: string;
  first_name: string;
  last_name: string;
}

export function HotelReimbursementsView() {
  const toast = useToast();
  const [reimbursements, setReimbursements] = useState<HotelReimbursement[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedReimbursement, setSelectedReimbursement] = useState<HotelReimbursement | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [reimbursementToDelete, setReimbursementToDelete] = useState<HotelReimbursement | null>(null);
  const [drivers, setDrivers] = useState<Driver[]>([]);
  const [driverSearch, setDriverSearch] = useState('');
  const [showDriverDropdown, setShowDriverDropdown] = useState(false);

  const [formData, setFormData] = useState({
    driver_name: '',
    date: new Date().toISOString().split('T')[0],
    hotel_description: '',
    who_pays: 'company' as 'company' | 'driver',
    amount: '',
    driver_paid: false,
  });

  useEffect(() => {
    loadReimbursements();
    loadDrivers();
  }, []);

  async function loadDrivers() {
    try {
      // Load all drivers including unassigned/inactive ones
      const { data, error } = await supabase
        .from('drivers')
        .select('id, first_name, last_name, driver_status')
        .order('first_name');

      if (error) throw error;
      setDrivers(data || []);
    } catch (error) {
      console.error('Error loading drivers:', error);
    }
  }

  async function loadReimbursements() {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('hotel_reimbursements')
        .select('*')
        .order('date', { ascending: false });

      if (error) throw error;

      setReimbursements(data || []);
    } catch (error) {
      console.error('Error loading hotel reimbursements:', error);
      toast.error('Failed to load hotel reimbursements');
    } finally {
      setLoading(false);
    }
  }


  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.driver_name || !formData.date || !formData.hotel_description || !formData.amount) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      const { error } = await supabase
        .from('hotel_reimbursements')
        .insert([{
          driver_name: formData.driver_name.trim(),
          date: formData.date,
          hotel_description: formData.hotel_description,
          who_pays: formData.who_pays,
          amount: parseFloat(formData.amount),
          driver_paid: formData.driver_paid,
        }]);

      if (error) throw error;

      toast.success('Hotel reimbursement added successfully!');
      setShowAddModal(false);
      resetForm();
      loadReimbursements();
    } catch (error) {
      console.error('Error adding hotel reimbursement:', error);
      toast.error('Failed to add hotel reimbursement');
    }
  };

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedReimbursement) return;

    try {
      const { error } = await supabase
        .from('hotel_reimbursements')
        .update({
          driver_name: formData.driver_name.trim(),
          date: formData.date,
          hotel_description: formData.hotel_description,
          who_pays: formData.who_pays,
          amount: parseFloat(formData.amount),
          driver_paid: formData.driver_paid,
        })
        .eq('id', selectedReimbursement.id);

      if (error) throw error;

      toast.success('Hotel reimbursement updated successfully!');
      setShowEditModal(false);
      setSelectedReimbursement(null);
      resetForm();
      loadReimbursements();
    } catch (error) {
      console.error('Error updating hotel reimbursement:', error);
      toast.error('Failed to update hotel reimbursement');
    }
  };

  const handleDelete = async () => {
    if (!reimbursementToDelete) return;

    try {
      const { error } = await supabase
        .from('hotel_reimbursements')
        .delete()
        .eq('id', reimbursementToDelete.id);

      if (error) throw error;

      toast.success('Hotel reimbursement deleted successfully!');
      setShowDeleteConfirm(false);
      setReimbursementToDelete(null);
      loadReimbursements();
    } catch (error) {
      console.error('Error deleting hotel reimbursement:', error);
      toast.error('Failed to delete hotel reimbursement');
    }
  };

  const resetForm = () => {
    setFormData({
      driver_name: '',
      date: new Date().toISOString().split('T')[0],
      hotel_description: '',
      who_pays: 'company',
      amount: '',
      driver_paid: false,
    });
  };

  const openEditModal = (reimbursement: HotelReimbursement) => {
    setSelectedReimbursement(reimbursement);
    setFormData({
      driver_name: reimbursement.driver_name,
      date: reimbursement.date,
      hotel_description: reimbursement.hotel_description,
      who_pays: reimbursement.who_pays,
      amount: reimbursement.amount.toString(),
      driver_paid: reimbursement.driver_paid,
    });
    setShowEditModal(true);
  };

  const toggleDriverPaid = async (reimbursement: HotelReimbursement) => {
    if (reimbursement.who_pays !== 'driver') return;

    try {
      const { error } = await supabase
        .from('hotel_reimbursements')
        .update({ driver_paid: !reimbursement.driver_paid })
        .eq('id', reimbursement.id);

      if (error) throw error;

      toast.success(`Payment status updated to ${!reimbursement.driver_paid ? 'Paid' : 'Not Paid'}`);
      loadReimbursements();
    } catch (error) {
      console.error('Error updating payment status:', error);
      toast.error('Failed to update payment status');
    }
  };

  const filteredReimbursements = reimbursements.filter(reimbursement => {
    const searchLower = searchTerm.toLowerCase();
    return (
      reimbursement.driver_name.toLowerCase().includes(searchLower) ||
      reimbursement.hotel_description.toLowerCase().includes(searchLower) ||
      reimbursement.date.includes(searchLower)
    );
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-white">
      {/* Header */}
      <div className="flex-none border-b border-gray-200 bg-white px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Hotel Reimbursements</h1>
            <p className="text-sm text-gray-500 mt-1">Manage hotel expenses and reimbursements</p>
          </div>
          <button
            onClick={() => {
              resetForm();
              setShowAddModal(true);
            }}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Plus className="w-5 h-5" />
            Add Hotel Reimbursement
          </button>
        </div>

        {/* Search Bar */}
        <div className="mt-4 relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
          <input
            type="text"
            placeholder="Search by driver name, hotel, or date..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
      </div>

      {/* Table */}
      <div className="flex-1 overflow-auto">
        <table className="w-full">
          <thead className="bg-gray-50 sticky top-0 z-10">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Driver
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Date
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Hotel Description
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Who Pays
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Amount
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Driver Paid
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredReimbursements.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-6 py-12 text-center text-gray-500">
                  <Building2 className="w-12 h-12 mx-auto mb-3 text-gray-400" />
                  <p className="text-lg font-medium">No hotel reimbursements found</p>
                  <p className="text-sm mt-1">Add your first hotel reimbursement to get started</p>
                </td>
              </tr>
            ) : (
              filteredReimbursements.map((reimbursement) => {
                const getRowColor = () => {
                  if (reimbursement.who_pays === 'company') return 'bg-white hover:bg-gray-50';
                  if (reimbursement.driver_paid) return 'bg-green-50 hover:bg-green-100';
                  return 'bg-red-50 hover:bg-red-100';
                };
                
                return (
                <tr key={reimbursement.id} className={getRowColor()}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <User className="w-5 h-5 text-gray-400 mr-2" />
                      <span className="text-sm font-medium text-gray-900">{reimbursement.driver_name}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <Calendar className="w-4 h-4 text-gray-400 mr-2" />
                      <span className="text-sm text-gray-900">
                        {new Date(reimbursement.date).toLocaleDateString()}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className="text-sm text-gray-900">{reimbursement.hotel_description}</span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      reimbursement.who_pays === 'company'
                        ? 'bg-blue-100 text-blue-800'
                        : 'bg-purple-100 text-purple-800'
                    }`}>
                      {reimbursement.who_pays === 'company' ? 'Company' : 'Driver'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <DollarSign className="w-4 h-4 text-gray-400" />
                      <span className="text-sm font-medium text-gray-900">
                        {reimbursement.amount.toFixed(2)}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-center">
                    {reimbursement.who_pays === 'driver' ? (
                      <div className="inline-flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={reimbursement.driver_paid}
                          onChange={() => toggleDriverPaid(reimbursement)}
                          className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 cursor-pointer"
                        />
                        {reimbursement.driver_paid ? (
                          <CheckCircle className="w-5 h-5 text-green-600" />
                        ) : (
                          <XCircle className="w-5 h-5 text-red-600" />
                        )}
                      </div>
                    ) : (
                      <span className="text-sm text-gray-400">N/A</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <button
                      onClick={() => openEditModal(reimbursement)}
                      className="text-blue-600 hover:text-blue-900 mr-3"
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => {
                        setReimbursementToDelete(reimbursement);
                        setShowDeleteConfirm(true);
                      }}
                      className="text-red-600 hover:text-red-900"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Add Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-xl font-bold text-gray-900">Add Hotel Reimbursement</h2>
              <button
                onClick={() => {
                  setShowAddModal(false);
                  resetForm();
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Driver <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    type="text"
                    value={driverSearch}
                    onChange={(e) => { setDriverSearch(e.target.value); setShowDriverDropdown(true); }}
                    onFocus={() => setShowDriverDropdown(true)}
                    placeholder="Search driver..."
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                {driverSearch && showDriverDropdown && (
                  <div className="mt-2 max-h-48 overflow-y-auto border border-gray-300 rounded-lg">
                    {drivers
                      .filter(d =>
                        `${d.first_name} ${d.last_name}`.toLowerCase().includes(driverSearch.toLowerCase())
                      )
                      .map((driver) => (
                        <button
                          key={driver.id}
                          type="button"
                          onClick={() => {
                            setFormData({ ...formData, driver_name: `${driver.first_name} ${driver.last_name}` });
                            setDriverSearch(`${driver.first_name} ${driver.last_name}`);
                            setShowDriverDropdown(false);
                          }}
                          className="w-full text-left px-4 py-2 hover:bg-gray-100 transition-colors"
                        >
                          {driver.first_name} {driver.last_name}
                        </button>
                      ))}
                  </div>
                )}
                {formData.driver_name && !driverSearch && (
                  <p className="mt-2 text-sm text-green-600">
                    ✓ Driver selected: {formData.driver_name}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Date <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Hotel Description <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={formData.hotel_description}
                  onChange={(e) => setFormData({ ...formData, hotel_description: e.target.value })}
                  rows={3}
                  placeholder="e.g., Holiday Inn Express, Chicago, IL - 2 nights"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Who Pays <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.who_pays}
                  onChange={(e) => setFormData({ ...formData, who_pays: e.target.value as 'company' | 'driver' })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                >
                  <option value="company">Company</option>
                  <option value="driver">Driver</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Amount <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    type="number"
                    step="0.01"
                    value={formData.amount}
                    onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                    placeholder="0.00"
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    required
                  />
                </div>
              </div>

              {formData.who_pays === 'driver' && (
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="driver_paid"
                    checked={formData.driver_paid}
                    onChange={(e) => setFormData({ ...formData, driver_paid: e.target.checked })}
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <label htmlFor="driver_paid" className="ml-2 text-sm text-gray-700">
                    Driver has paid
                  </label>
                </div>
              )}

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowAddModal(false);
                    resetForm();
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Add Reimbursement
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && selectedReimbursement && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-xl font-bold text-gray-900">Edit Hotel Reimbursement</h2>
              <button
                onClick={() => {
                  setShowEditModal(false);
                  setSelectedReimbursement(null);
                  resetForm();
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleUpdate} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Driver Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.driver_name}
                  onChange={(e) => setFormData({ ...formData, driver_name: e.target.value })}
                  placeholder="Enter driver name"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Date <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Hotel Description <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={formData.hotel_description}
                  onChange={(e) => setFormData({ ...formData, hotel_description: e.target.value })}
                  rows={3}
                  placeholder="e.g., Holiday Inn Express, Chicago, IL - 2 nights"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Who Pays <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.who_pays}
                  onChange={(e) => setFormData({ ...formData, who_pays: e.target.value as 'company' | 'driver' })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                >
                  <option value="company">Company</option>
                  <option value="driver">Driver</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Amount <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    type="number"
                    step="0.01"
                    value={formData.amount}
                    onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                    placeholder="0.00"
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    required
                  />
                </div>
              </div>

              {formData.who_pays === 'driver' && (
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="driver_paid_edit"
                    checked={formData.driver_paid}
                    onChange={(e) => setFormData({ ...formData, driver_paid: e.target.checked })}
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <label htmlFor="driver_paid_edit" className="ml-2 text-sm text-gray-700">
                    Driver has paid
                  </label>
                </div>
              )}

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowEditModal(false);
                    setSelectedReimbursement(null);
                    resetForm();
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Update Reimbursement
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && reimbursementToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Delete Hotel Reimbursement</h3>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete this hotel reimbursement for{' '}
              <span className="font-semibold">{reimbursementToDelete.driver_name}</span>?
              This action cannot be undone.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setReimbursementToDelete(null);
                }}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
